import React, { useState } from 'react'
import {
    Box,
    Button,
    Chip,
    Link,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'

import { Lead } from '../../../../models/Outreach/types'
import {
    canResendScreeningInvitation,
    canStartScreening,
    getBackgroundCheckDisplay,
    getScreeningRequestStatusDisplay,
    getScreeningResultDisplay,
} from '../../../../utils/screeningStatus'

const STATUS_CHIP_COLORS = {
    neutral: { bg: '#f3f4f6', fg: '#6b7280' },
    good: { bg: '#d1fae5', fg: '#047857' },
    warn: { bg: '#fef3c7', fg: '#b45309' },
    bad: { bg: '#ffe4e6', fg: '#be123c' },
} as const

function formatDateTime(iso: string | null | undefined): string {
    if (!iso) return '—'
    try {
        return new Date(iso).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        })
    } catch {
        return '—'
    }
}

const useStyles = makeStyles((theme: Theme) => ({
    section: {
        marginBottom: theme.spacing(3),
    },
    sectionTitle: {
        fontSize: '1.125rem',
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        color: theme.palette.text.primary,
        paddingBottom: theme.spacing(1),
        borderBottom: `2px solid ${theme.palette.primary.main}`,
    },
    fieldRow: {
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: theme.spacing(1.5),
        gap: theme.spacing(2),
    },
    fieldLabel: {
        minWidth: 160,
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        fontWeight: 600,
    },
    fieldValue: {
        flex: 1,
        fontSize: '0.875rem',
        wordBreak: 'break-word',
    },
    actions: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    inviteUrl: {
        fontFamily: 'monospace',
        fontSize: '0.75rem',
        wordBreak: 'break-all',
    },
}))

interface LeadScreeningPanelProps {
    lead: Lead
    onStartScreening?: (leadId: number) => Promise<unknown>
    onResendScreeningInvitation?: (leadId: number) => Promise<unknown>
    isStarting?: boolean
    isResending?: boolean
}

export const LeadScreeningPanel: React.FC<LeadScreeningPanelProps> = ({
    lead,
    onStartScreening,
    onResendScreeningInvitation,
    isStarting = false,
    isResending = false,
}) => {
    const classes = useStyles()
    const [copied, setCopied] = useState(false)
    const screening = lead.screening_request
    const statusDisplay = getScreeningRequestStatusDisplay(screening?.status)
    const resultDisplay = getScreeningResultDisplay(lead.screening_result)
    const backgroundDisplay = getBackgroundCheckDisplay(lead.background_check)
    const statusColors = STATUS_CHIP_COLORS[statusDisplay.tone]
    const resultColors = STATUS_CHIP_COLORS[resultDisplay.tone]
    const backgroundColors = STATUS_CHIP_COLORS[backgroundDisplay.tone]

    const showStart =
        onStartScreening &&
        canStartScreening({
            pipelineStage: lead.pipeline_stage,
            screeningStatus: screening?.status,
            screeningResult: lead.screening_result,
        })
    const showResend =
        onResendScreeningInvitation &&
        canResendScreeningInvitation({
            pipelineStage: lead.pipeline_stage,
            screeningStatus: screening?.status,
            invitationUrl: screening?.invitation_url,
            candidateId: screening?.checkr_candidate_id,
        })

    const handleCopyInvite = async () => {
        const url = screening?.invitation_url
        if (!url) return
        try {
            await navigator.clipboard.writeText(url)
            setCopied(true)
            window.setTimeout(() => setCopied(false), 2000)
        } catch {
            setCopied(false)
        }
    }

    if (lead.pipeline_stage !== 'screening' && !screening) {
        return (
            <Box className={classes.section}>
                <Typography className={classes.sectionTitle}>
                    Screening
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Screening actions are available when this lead is in the
                    Screening stage.
                </Typography>
            </Box>
        )
    }

    return (
        <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>Screening</Typography>

            <Box className={classes.actions}>
                {showStart && (
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={isStarting}
                        onClick={() => onStartScreening(lead.id)}
                    >
                        {isStarting
                            ? 'Starting…'
                            : screening
                            ? 'Start new screening'
                            : 'Start screening'}
                    </Button>
                )}
                {showResend && (
                    <Button
                        color="primary"
                        variant="outlined"
                        disabled={isResending}
                        onClick={() => onResendScreeningInvitation(lead.id)}
                    >
                        {isResending
                            ? 'Working…'
                            : screening?.status === 'INVITED'
                            ? 'Get invite link'
                            : 'Resend invitation'}
                    </Button>
                )}
                {screening?.invitation_url && (
                    <Button variant="outlined" onClick={handleCopyInvite}>
                        {copied ? 'Copied' : 'Copy invite URL'}
                    </Button>
                )}
                {lead.screening_report_url && (
                    <Button
                        variant="outlined"
                        color="primary"
                        href={lead.screening_report_url}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Open report
                    </Button>
                )}
            </Box>

            {screening?.last_error && (
                <Box mb={2}>
                    <Alert severity="error">{screening.last_error}</Alert>
                </Box>
            )}

            {lead.screening_result === 'conditional' && (
                <Box mb={2}>
                    <Alert severity="warning">
                        Checkr returned Consider — review the report before
                        advancing this lead.
                    </Alert>
                </Box>
            )}

            <Box className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Status</Typography>
                <Box style={{ flex: 1 }}>
                    <Chip
                        size="small"
                        label={statusDisplay.label}
                        style={{
                            backgroundColor: statusColors.bg,
                            color: statusColors.fg,
                            fontWeight: 600,
                        }}
                    />
                </Box>
            </Box>

            <Box className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Result</Typography>
                <Box style={{ flex: 1 }}>
                    <Chip
                        size="small"
                        label={resultDisplay.label}
                        style={{
                            backgroundColor: resultColors.bg,
                            color: resultColors.fg,
                            fontWeight: 600,
                        }}
                    />
                </Box>
            </Box>

            <Box className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>
                    Background
                </Typography>
                <Box style={{ flex: 1 }}>
                    <Chip
                        size="small"
                        label={backgroundDisplay.label}
                        style={{
                            backgroundColor: backgroundColors.bg,
                            color: backgroundColors.fg,
                            fontWeight: 600,
                        }}
                    />
                </Box>
            </Box>

            <Box className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>Credit</Typography>
                <Typography className={classes.fieldValue}>
                    {lead.credit_score ?? '—'}
                </Typography>
            </Box>

            {screening && (
                <>
                    <Box className={classes.fieldRow}>
                        <Typography className={classes.fieldLabel}>
                            Package
                        </Typography>
                        <Typography className={classes.fieldValue}>
                            {screening.package || '—'}
                        </Typography>
                    </Box>
                    <Box className={classes.fieldRow}>
                        <Typography className={classes.fieldLabel}>
                            Invited
                        </Typography>
                        <Typography className={classes.fieldValue}>
                            {formatDateTime(screening.invited_at)}
                        </Typography>
                    </Box>
                    <Box className={classes.fieldRow}>
                        <Typography className={classes.fieldLabel}>
                            Completed
                        </Typography>
                        <Typography className={classes.fieldValue}>
                            {formatDateTime(screening.completed_at)}
                        </Typography>
                    </Box>
                    {screening.invitation_url && (
                        <Box className={classes.fieldRow}>
                            <Typography className={classes.fieldLabel}>
                                Invite URL
                            </Typography>
                            <Typography
                                className={`${classes.fieldValue} ${classes.inviteUrl}`}
                            >
                                <Link
                                    href={screening.invitation_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {screening.invitation_url}
                                </Link>
                            </Typography>
                        </Box>
                    )}
                </>
            )}

            {!screening && lead.pipeline_stage === 'screening' && (
                <Typography variant="body2" color="textSecondary">
                    No Checkr invitation yet. Start screening to create a Hosted
                    Apply link for this applicant.
                </Typography>
            )}
        </Box>
    )
}
